import clsx from 'clsx';
import type * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Card,
    CardLink,
    Description,
    Skeleton,
    TextSkeleton,
    Tooltip,
} from '@gitbook/spine-react';

import { useNavigation, useNavigationState } from '../../navigation';
import bgDark from './assets/enhanced-card-bg-dark.raw.svg';
import bgLight from './assets/enhanced-card-bg-light.raw.svg';
import { IntegrationInstallationStatusBadge } from './IntegrationInstallationStatusBadge';
import { IntegrationTileIcon } from './IntegrationTileIcon';
import { INTEGRATIONS_CATEGORY_PROPS } from './integrations.constants';

export type IntegrationCardKind = 'minimal' | 'default' | 'enhanced';

/**
 * Card displaying an integration's details.
 */
export function IntegrationCard(props: {
    kind?: IntegrationCardKind;
    /** Whether it was displayed as a featured or recommended integration. */
    featured?: boolean;
    organizationId: string;
    integration: api.Integration;
    /** Integration installation status if already installed in the current space/site */
    contentInstallationStatus?: api.IntegrationInstallationStatus;
}): React.ReactElement | null {
    const { kind = 'default', featured = false, integration, contentInstallationStatus } = props;
    const { screen } = useNavigationState();
    const { navigate } = useNavigation();

    const specs = KIND_SPECS[kind];

    return (
        <>
            <CardLink
                className={clsx(getCardClassName({ kind }), specs.bgGradient)}
                style={
                    specs.bgGradient
                        ? ({
                              '--bg-light': `url(${bgLight})`,
                              '--bg-dark': `url(${bgDark})`,
                          } as React.CSSProperties)
                        : undefined
                }
                aria-label={`Go to ${integration.title} integration details`}
                onPress={() => {
                    const eventBase = {
                        type: 'integration_featured_select',
                        integration: integration.name,
                    } as const;

                    switch (screen.type) {
                        case 'space': {
                            if (featured) {
                                analytics.logEvent(
                                    analytics.createSpaceEvent(
                                        {
                                            key: screen.space,
                                            organization: screen.organization,
                                        },
                                        eventBase
                                    )
                                );
                            }

                            navigate({
                                screen: {
                                    type: 'space',
                                    space: screen.space,
                                    organization: screen.organization,
                                    sideSheet: {
                                        type: 'integrations',
                                        view: {
                                            type: 'integration',
                                            integration: integration.name,
                                        },
                                    },
                                },
                            });
                            break;
                        }
                        case 'site': {
                            if (featured) {
                                analytics.logEvent(
                                    analytics.createSiteEvent(
                                        {
                                            id: screen.site,
                                            organization: screen.organization,
                                        },
                                        eventBase
                                    )
                                );
                            }

                            navigate({
                                screen: {
                                    type: 'site',
                                    site: screen.site,
                                    organization: screen.organization,
                                    sideSheet: {
                                        type: 'integrations',
                                        view: {
                                            type: 'integration',
                                            integration: integration.name,
                                        },
                                    },
                                },
                            });
                            break;
                        }
                        default: {
                            if (featured && 'organization' in screen && screen.organization) {
                                analytics.logEvent(
                                    analytics.createOrgEvent(screen.organization, eventBase)
                                );
                            }

                            navigate({
                                screen: { type: 'integration', integration: integration.name },
                            });
                        }
                    }
                }}
            >
                <div className="flex items-start gap-4">
                    <div
                        className={clsx(
                            'flex flex-1 justify-between gap-6 max-md:flex-col',
                            specs.category && specs.cta ? 'items-center' : 'items-start'
                        )}
                    >
                        <div
                            className={clsx(
                                'flex',
                                kind === 'enhanced' && 'gap-4 max-sm:items-center sm:flex-col',
                                kind === 'minimal' && 'flex-row items-center gap-6',
                                kind === 'default' && 'flex-row items-center gap-3'
                            )}
                        >
                            <IntegrationTileIcon size={specs.tileSize} integration={integration} />
                            <div className="flex items-center gap-3">
                                <IntegrationCardHeading kind={kind}>
                                    {integration.title}
                                </IntegrationCardHeading>
                                {integration.visibility === api.IntegrationVisibility.Private ? (
                                    <Tooltip label="This integration is private and only visible to your organization">
                                        <Badge
                                            iconOnly
                                            kind="secondary"
                                            icon={<Icon.Lock className="size-3" />}
                                            aria-label="Private integration"
                                            className="m-nudge-icon"
                                        />
                                    </Tooltip>
                                ) : null}
                            </div>
                        </div>

                        {specs.category ? (
                            <Badge
                                label={INTEGRATIONS_CATEGORY_PROPS[integration.categories[0]].label}
                                kind="secondary"
                                className="max-md:-order-1"
                            />
                        ) : null}
                    </div>

                    {!contentInstallationStatus && specs.cta ? (
                        <Button
                            iconOnly
                            kind="secondary"
                            label={`Install ${integration.title} integration`}
                            leadingIcon={Icon.IntegrationsInstall}
                            href={
                                screen.type === 'space' || screen.type === 'site'
                                    ? {
                                          current: {
                                              sideSheet: {
                                                  type: 'integrations',
                                                  view: {
                                                      type: 'integration',
                                                      integration: integration.name,
                                                  },
                                              },
                                          },
                                      }
                                    : {
                                          screen: {
                                              type: 'integration',
                                              integration: integration.name,
                                          },
                                      }
                            }
                        />
                    ) : contentInstallationStatus ? (
                        <IntegrationInstallationStatusBadge
                            iconOnly
                            isActive={
                                contentInstallationStatus ===
                                api.IntegrationInstallationStatus.Active
                            }
                        />
                    ) : null}
                </div>

                {specs.description ? <Description>{integration.description}</Description> : null}
            </CardLink>

            <link rel="preload" as="image" href={bgLight} />
            <link rel="preload" as="image" href={bgDark} />
        </>
    );
}

function getCardClassName(props: { kind?: IntegrationCardKind }) {
    const { kind = 'default' } = props;
    return clsx(
        'flex h-full flex-col gap-4',
        kind === 'enhanced' ? 'max-sm:p-4 sm:px-4 sm:py-8' : 'p-4'
    );
}

export function IntegrationCardSkeleton(props: { kind?: IntegrationCardKind }) {
    const { kind = 'default' } = props;
    const specs = KIND_SPECS[kind];
    return (
        <Card className={getCardClassName(props)}>
            <Skeleton shape="circle" style={{ width: specs.tileSize, height: specs.tileSize }}>
                <Icon.Integrations />
            </Skeleton>
            <IntegrationCardHeading kind={kind}>
                <TextSkeleton>Integration</TextSkeleton>
            </IntegrationCardHeading>
            <Description>
                <TextSkeleton className="w-full">First line of description</TextSkeleton>
                <TextSkeleton>Second line of description</TextSkeleton>
            </Description>
        </Card>
    );
}

function IntegrationCardHeading(props: { kind?: IntegrationCardKind; children: React.ReactNode }) {
    const { kind = 'default', children } = props;

    return <h3 className={clsx(KIND_SPECS[kind].labelStyle)}>{children}</h3>;
}

const KIND_SPECS = {
    minimal: {
        labelStyle: 'text-ui-heading-small',
        tileSize: 48,
        description: false,
        category: false,
        cta: false,
        bgGradient: '',
    },
    default: {
        labelStyle: 'text-ui-standout',
        tileSize: 32,
        description: true,
        category: false,
        cta: true,
        bgGradient: '',
    },
    enhanced: {
        labelStyle: 'text-ui-heading-medium',
        tileSize: 40,
        description: true,
        category: true,
        cta: false,
        bgGradient:
            'bg-[image:var(--bg-light)] bg-[1px] bg-cover bg-no-repeat dark:bg-[image:var(--bg-dark)]',
    },
};
